import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui-kit/dialog';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { useGetTools } from '@/modules/gpt-chats/hooks/use-tools';
import { showErrorToast, showSuccessToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Wrench, Search } from 'lucide-react';
import { useState } from 'react';
import { Card } from '@/components/ui-kit/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import {
  useGetAgentById,
  useUpdateAgentAIConfigurations,
} from '@/modules/gpt-chats/hooks/use-agents';
import { getToolIcon } from '@/modules/gpt-chats/utils/tool-utils';

interface AgentConfigAddToolModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentId: string;
}

export const AgentConfigAddToolModal = ({
  open,
  onOpenChange,
  agentId,
}: AgentConfigAddToolModalProps) => {
  const { t } = useTranslation();
  const [toolType, setToolType] = useState<'tool' | 'mcp-server'>('tool');
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingToolId, setUpdatingToolId] = useState<string | null>(null);

  const { data: agentData } = useGetAgentById(agentId);
  const { mutateAsync: updateAgentConfig } = useUpdateAgentAIConfigurations();

  const agent = agentData?.agent;
  const currentToolIds = agent?.tool_ids || [];

  const apiToolType =
    toolType === 'tool' ? 'api' : toolType === 'mcp-server' ? 'mcp_server' : toolType;

  const { data: toolsData, isLoading: isLoadingTools } = useGetTools({
    tool_type: apiToolType as 'api' | 'mcp_server',
    page: 1,
    page_size: 50,
  });

  const tools = toolsData?.tools || [];
  const filteredTools = tools.filter((tool) =>
    tool.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedCountForCurrentTab = filteredTools.filter((tool) =>
    currentToolIds.includes(tool.id)
  ).length;

  const handleAddTool = async (toolId: string) => {
    if (!agent || updatingToolId) return;

    setUpdatingToolId(toolId);

    try {
      const newToolIds = [...currentToolIds, toolId];

      const payload = {
        agent_id: agentId,
        rag_config: agent.rag_config,
        llm_config: agent.llm_config,
        guardrail: agent.guardrail,
        tool_ids: newToolIds,
        enable_tools: true,
      };

      await updateAgentConfig(payload);

      showSuccessToast({
        description: t('TOOL_ADDED_SUCCESSFULLY'),
      });
    } catch (error) {
      showErrorToast({
        title: t('FAILED_TO_ADD_TOOL'),
        errors: error,
      });
    } finally {
      setUpdatingToolId(null);
    }
  };

  const handleRemoveTool = async (toolId: string) => {
    if (!agent || updatingToolId) return;

    setUpdatingToolId(toolId);

    try {
      const newToolIds = currentToolIds.filter((id) => id !== toolId);

      const payload = {
        agent_id: agentId,
        rag_config: agent.rag_config,
        llm_config: agent.llm_config,
        guardrail: agent.guardrail,
        tool_ids: newToolIds,
        enable_tools: newToolIds.length > 0,
      };

      await updateAgentConfig(payload);

      showSuccessToast({
        description: t('TOOL_REMOVED_SUCCESSFULLY'),
      });
    } catch (error) {
      showErrorToast({
        title: t('FAILED_TO_REMOVE_TOOL'),
        errors: error,
      });
    } finally {
      setUpdatingToolId(null);
    }
  };

  const handleClose = () => {
    setToolType('tool');
    setSearchQuery('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[800px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">{t('ADD_TOOL')}</DialogTitle>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('ADD_TOOL_DESCRIPTION', {
              type: toolType === 'mcp-server' ? t('MCP_SERVERS') : t('TOOLS'),
            })}
          </p>
        </DialogHeader>

        <div className="mt-6 space-y-6">
          <Tabs value={toolType} onValueChange={(v) => setToolType(v as 'tool' | 'mcp-server')}>
            <TabsList>
              <TabsTrigger value="tool">{t('TOOL')}</TabsTrigger>
              <TabsTrigger value="mcp-server">{t('MCP_SERVER')}</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('SEARCH_TOOLS_PLACEHOLDER')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="h-[400px] space-y-2 overflow-y-auto">
            {isLoadingTools ? (
              <>
                {Array.from({ length: 3 }).map((_, index) => (
                  <Card key={index} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 rounded" />
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-48" />
                        </div>
                      </div>
                      <Skeleton className="h-6 w-10" />
                    </div>
                  </Card>
                ))}
              </>
            ) : filteredTools.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-center">
                <Wrench className="mb-4 h-12 w-12 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {searchQuery
                    ? t('NO_TOOLS_FOUND_MATCHING_SEARCH', {
                        type: toolType === 'mcp-server' ? t('MCP_SERVERS') : t('TOOLS'),
                      })
                    : t('NO_TOOLS_AVAILABLE', {
                        type: toolType === 'mcp-server' ? t('MCP_SERVERS') : t('TOOLS'),
                      })}
                </p>
              </div>
            ) : (
              filteredTools.map((tool) => {
                const isSelected = currentToolIds.includes(tool.id);
                const isThisToolUpdating = updatingToolId === tool.id;

                return (
                  <Card key={tool.id} className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 flex-1 items-start gap-3">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-primary">
                          {getToolIcon(tool.type)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-high-emphasis">{tool.name}</div>
                          <div className="line-clamp-2 text-sm text-muted-foreground">
                            {tool.description || t('NO_DESCRIPTION')}
                          </div>
                        </div>
                      </div>

                      {isSelected ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveTool(tool.id)}
                          disabled={updatingToolId !== null}
                          className="flex-shrink-0"
                        >
                          {isThisToolUpdating ? t('REMOVING') : t('REMOVE')}
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAddTool(tool.id)}
                          disabled={updatingToolId !== null}
                          className="flex-shrink-0"
                        >
                          {isThisToolUpdating ? t('ADDING') : t('ADD')}
                        </Button>
                      )}
                    </div>
                  </Card>
                );
              })
            )}
          </div>

          <div className="h-5">
            <div className="text-sm text-muted-foreground">
              {t(
                toolType === 'mcp-server' ? 'MCP_SERVERS_SELECTED_COUNT' : 'TOOLS_SELECTED_COUNT',
                { count: selectedCountForCurrentTab }
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
